import IPostMessage from '../IPostMessage';
import { IDeviceInfo, IDeviceLocation, IDeviceTag, IOrganizationTag } from './IDeviceInfo';

/**
 * The `sos.deviceInfo` API groups together methods for retrieving information about the device set in the CloudControl, such as location or
 * tags.
 */
export default class DeviceInfo implements IDeviceInfo {
	public static MESSAGE_PREFIX: string = 'device_info';

	/** @internal */
	constructor(
		private messagePrefix: string,
		private postMessage: IPostMessage<any>,
	) {}

	/**
	 * The `getLocation()` method returns location of the device with child tags if available. Location is requested from server on application start, automatically
	 * updated when changed and persisted in local storage.
	 *
	 * :::note
	 * Location can be set only in CloudControl on Device Info page, or via [Rest API](https://developers.signageos.io/api/#tag/DeviceLocation/paths/~1v1~1device~1%7BdeviceUid%7D~1location~1%7BlocationUid%7D/get).
	 * :::
	 *
	 * @returns {Promise<IDeviceLocation | null>} A promise that resolves to the device location or `null` if not set.
	 * @since 5.2.0
	 */
	public async getLocation(): Promise<IDeviceLocation | null> {
		const { location } = await this.postMessage({ type: this.getMessage('get_location') });
		return location;
	}

	/**
	 * @deprecated Please use `getDeviceTags()` method instead.
	 */
	public async getOrganizationTags(): Promise<IOrganizationTag[]> {
		const { tags } = await this.postMessage({ type: this.getMessage('get_organization_tags') });
		return tags;
	}

	/**
	 * The `getDeviceName()` method returns a name of the device. Name is requested from server on application start, automatically
	 * updated when changed and persisted in local storage.
	 *
	 * :::note
	 * Device name can be set only in CloudControl on Device Info page, or via [Rest API](https://developers.signageos.io/api/#tag/Device/paths/~1v2~1device~1%7BdeviceUid%7D/get).
	 * :::
	 *
	 * @returns {Promise<string>} A promise that resolves to the device name.
	 * @since 6.6.0
	 *
	 * @example
	 * const deviceName = await sos.deviceInfo.getDeviceName();
	 * console.log(`Device name is: ${deviceName}`);
	 */
	public async getDeviceName(): Promise<string | null> {
		const deviceNameFromTittle = this.geDeviceNameFromTittle();
		if (typeof deviceNameFromTittle === 'string') {
			return deviceNameFromTittle;
		}
		const { name }: { name: string | null } = await this.postMessage({ type: this.getMessage('get_device_name') });
		return name;
	}

	/**
	 * The `getDeviceTags()` method returns all organization tags assigned to the device including parent tags, if available.
	 * Tags are requested from server on application start, automatically updated when changed and persisted in local storage.
	 *
	 * :::note
	 * Tags can be set only in CloudControl on Device Info page, or via [Rest API](https://developers.signageos.io/api/#tag/DeviceTag-(Device-Tags)).
	 * :::
	 *
	 * @returns {Promise<IDeviceTag[]>} A promise that returns the device tags.
	 * @since 8.5.0
	 */
	public async getDeviceTags(): Promise<IDeviceTag[]> {
		const { tags } = await this.postMessage({ type: this.getMessage('get_device_tags') });
		return tags;
	}

	private getMessage(name: string) {
		return [this.messagePrefix, DeviceInfo.MESSAGE_PREFIX, name].join('.');
	}

	private geDeviceNameFromTittle() {
		try {
			const rootDocument = window.parent.document;
			const titleElement = rootDocument.head.getElementsByTagName('title')[0];
			const parentElement = titleElement.parentElement!;
			return parentElement.getElementsByTagName('title')[0].innerHTML || null;
		} catch {
			return null;
		}
	}
}
